import * as prismicCustomTypesClient from "@prismicio/custom-types-client";
import fetch from './../../_node_modules/node-fetch/src/index.js';
import * as z from "zod";
import { API_ENDPOINTS } from "../../constants/API_ENDPOINTS.js";
import { PRISMIC_CLI_USER_AGENT } from "../../constants/PRISMIC_CLI_USER_AGENT.js";
import { assertPluginsInitialized } from "../../lib/assertPluginsInitialized.js";
import { decodeHookResult } from "../../lib/decodeHookResult.js";
import { SharedSliceSchema } from "../../lib/typesInternalSchemas.js";
import { BaseManager } from "../BaseManager.js";
class SlicesManager extends BaseManager {
  async readSliceLibrary(args) {
    assertPluginsInitialized(this.pluginSystemRunner);
    const hookResult = await this.pluginSystemRunner.callHook("slice-library:read", args);
    const { data, errors } = decodeHookResult(z.object({
      id: z.string(),
      sliceIDs: z.array(z.string())
    }), hookResult);
    return {
      sliceIDs: data[0]?.sliceIDs ?? [],
      errors
    };
  }
  async getDefaultLibraryID() {
    const prismicConfig = await this.project.getPrismicConfig();
    const libraryIDs = prismicConfig.libraries || [];
    return libraryIDs[0];
  }
  async readAllSliceLibraries() {
    assertPluginsInitialized(this.pluginSystemRunner);
    const prismicConfig = await this.project.getPrismicConfig();
    const libraryIDs = prismicConfig.libraries || [];
    const res = {
      libraries: [],
      errors: []
    };
    for (const libraryID of libraryIDs) {
      const { sliceIDs, errors } = await this.readSliceLibrary({
        libraryID
      });
      res.errors = [...res.errors, ...errors];
      res.libraries.push({
        libraryID,
        sliceIDs
      });
    }
    return res;
  }
  async readAllSlicesForLibrary(args) {
    assertPluginsInitialized(this.pluginSystemRunner);
    const res = {
      models: [],
      errors: []
    };
    const { sliceIDs, errors } = await this.readSliceLibrary({
      libraryID: args.libraryID
    });
    res.errors.push(...errors);
    if (sliceIDs) {
      for (const sliceID of sliceIDs) {
        const { model, errors: errors2 } = await this.readSlice({
          libraryID: args.libraryID,
          sliceID
        });
        res.errors.push(...errors2);
        if (model) {
          res.models.push({ model });
        }
      }
    }
    return res;
  }
  async readAllSlices() {
    assertPluginsInitialized(this.pluginSystemRunner);
    const prismicConfig = await this.project.getPrismicConfig();
    const libraryIDs = prismicConfig.libraries || [];
    const res = {
      models: [],
      errors: []
    };
    for (const libraryID of libraryIDs) {
      const { models, errors } = await this.readAllSlicesForLibrary({
        libraryID
      });
      res.errors.push(...errors);
      for (const model of models) {
        res.models.push({
          libraryID,
          model: model.model
        });
      }
    }
    return res;
  }
  async createSlice(args) {
    assertPluginsInitialized(this.pluginSystemRunner);
    const hookResult = await this.pluginSystemRunner.callHook("slice:create", args);
    return {
      errors: hookResult.errors
    };
  }
  async readSlice(args) {
    assertPluginsInitialized(this.pluginSystemRunner);
    const hookResult = await this.pluginSystemRunner.callHook("slice:read", args);
    const { data, errors } = decodeHookResult(z.object({
      model: SharedSliceSchema
    }), hookResult);
    return {
      model: data[0]?.model,
      errors: errors.map((error) => {
        error.message = `Failed to decode slice model with id '${args.sliceID}': ${error.message}`;
        return error;
      })
    };
  }
  async updateSlice(args) {
    assertPluginsInitialized(this.pluginSystemRunner);
    const hookResult = await this.pluginSystemRunner.callHook("slice:update", args);
    return {
      errors: [...hookResult.errors]
    };
  }
  async renameSlice(args) {
    assertPluginsInitialized(this.pluginSystemRunner);
    const hookResult = await this.pluginSystemRunner.callHook("slice:rename", args);
    return {
      errors: hookResult.errors
    };
  }
  async deleteSlice(args) {
    assertPluginsInitialized(this.pluginSystemRunner);
    const { model, errors: readSliceErrors } = await this.readSlice({
      libraryID: args.libraryID,
      sliceID: args.sliceID
    });
    if (model) {
      const { errors: deleteSliceErrors } = await this.pluginSystemRunner.callHook("slice:delete", {
        model,
        libraryID: args.libraryID
      });
      return {
        errors: deleteSliceErrors
      };
    } else {
      return {
        errors: readSliceErrors
      };
    }
  }
  async fetchRemoteSlices() {
    const authenticationToken = await this.user.getAuthenticationToken();
    const repositoryName = await this.project.getRepositoryName();
    const client = prismicCustomTypesClient.createClient({
      endpoint: API_ENDPOINTS.PrismicLegacyCustomTypesApi,
      repositoryName,
      token: authenticationToken,
      fetch,
      fetchOptions: {
        headers: {
          "User-Agent": PRISMIC_CLI_USER_AGENT
        }
      }
    });
    return await client.getAllSharedSlices();
  }
}
export {
  SlicesManager
};
//# sourceMappingURL=SlicesManager.js.map
